<template>
  <div class="page">
    <div v-if="getModuleConfig('welcome_page.step_section.enable_step_section')" class="p-3">
      <SharedStep :steps="getModuleConfig('welcome_page.step_section.steps')" />
    </div>

    <div v-if="getModuleConfig('welcome_page.form_title')" class="page__title">
      {{ getModuleConfig("welcome_page.form_title") }}
    </div>
    <div v-if="getModuleConfig('welcome_page.form_desc')" class="page__desc">
      {{ getModuleConfig("welcome_page.form_desc") }}
    </div>

    <div class="page__content" v-if="contentData" v-html="contentData"></div>

    <img
      v-for="image in contentImages"
      :src="image.image_url"
      :key="image.order"
      class="w-100"
    />

    <div class="page__button s-space-y-4">
      <template v-if="showRedirectButton">
        <MemberButton
          v-if="redirectAction"
          button-style="s-btn-outline-primary"
          :button-text="redirectActionConfig.button_text"
          :type="redirectAction"
        ></MemberButton>
        <SharedButton class="s-btn-outline-primary bg-transparent" @click="redirectToIntended" v-else>
          {{ redirectActionConfig.button_text }}
        </SharedButton>
      </template>
      <MemberButton
        v-if="showSuccessButton"
        :button-text="getModuleConfig('welcome_page.button_text')"
        :button-url="getModuleConfig('welcome_page.button_url')"
        :type="getModuleConfig('welcome_page.button_action')"
      ></MemberButton>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import SharedButton from "@/components/Page/Liff/Shared/Button.vue";
import MemberButton from "@/components/Page/Liff/Shared/MemberButton.vue";
import SharedStep from "@/components/Page/Liff/Shared/Steps";
import StringVariableParser from "@/utils/StringVariableParser";
import liff from "@line/liff";
import registerApi from "@/apis/liff/v2/register";

const BUTTON_TYPE = {
  SUCCESS: "success_button",
  REDIRECT: "redirect_button",
};

export default {
  components: {
    SharedButton,
    SharedStep,
    MemberButton,
  },
  computed: {
    ...mapGetters({
      hasIntended: "route/hasIntended",
      buttonDisplayList: "route/buttonDisplayList",
      redirectAction: "route/redirectAction",
      liffId: "liffGeneral/liffId",
    }),
    ...mapGetters("liffAuth", ["user"]),
    customer() {
      return this.user || {};
    },
    contentImages() {
      let contentImages = this.getModuleConfig("welcome_page.content_images") || []
      contentImages.sort((a, b) => a.order - b.order)
      return contentImages
    },
    redirectActionConfig() {
      return this.getModuleConfig("welcome_page.redirect_action") || {
        enable: false,
        button_type: [],
      };
    },
    redirectActionEnabled() {
      return this.hasIntended && this.redirectActionConfig.enable;
    },
    showSuccessButton() {
      return !!this.buttonDisplayList.success_button
        || !this.redirectActionEnabled
        || this.redirectActionConfig.button_type.includes(BUTTON_TYPE.SUCCESS);
    },
    showRedirectButton() {
      return !!this.buttonDisplayList.redirect_button
        || this.redirectAction
        || (this.hasIntended && this.redirectActionConfig.enable && this.redirectActionConfig.button_type.includes(BUTTON_TYPE.REDIRECT))
    },
    skipWelcomePage() {
      const allowedButtonTypes = Object.values(BUTTON_TYPE);

      return this.redirectActionEnabled && this.redirectActionConfig.button_type.filter((type) => allowedButtonTypes.includes(type)).length === 0;
    },
    showPage() {
      return this.getModuleConfig('welcome_page.step_section.enable_step_section')
        || this.getModuleConfig('welcome_page.form_title')
        || this.getModuleConfig('welcome_page.form_desc')
        || this.content
    },
    contentData() {
      return this.getModuleConfig("welcome_page.content") ?? false;
    },
  },
  async created() {
    try {
      // 顯示用戶資料
      console.log('用戶資料:', this.user);

      // 檢查是否啟用發送模板訊息功能
      if (this.getModuleConfig('welcome_page.send_templates.enable')) {
        console.log('發送模板訊息功能已啟用，呼叫 send-welcome message API');
        try {
          await registerApi.sendWelcomeMessage();
          console.log('send-welcome message API 呼叫成功');
        } catch (error) {
          console.error('send-welcome message API 呼叫失敗:', error);
        }
      }

      // 檢查並儲存同意條款版本 (批次處理)
      const termsConfig = this.getModuleConfig('terms_page') || {};
      if (termsConfig.enable && Array.isArray(termsConfig.content)) {
        console.log('正在批次儲存同意條款版本...');
        try {
          const res = await registerApi.saveTermConsent({
            content: termsConfig.content
          });
          console.log('同意條款批次儲存成功:', res.data);
        } catch (error) {
          console.error('同意條款批次儲存失敗:', error);
        }
      }

      await this.sendLiffMessage();
    } catch (error) {
      console.error('Failed to initialize LIFF or send message:', error);
    }

    if (this.skipWelcomePage) {
      await this.redirectToIntended();
    }
  },
  methods: {
    ...mapActions({
      redirectToIntended: "route/intended",
    }),
    getModuleConfig(key) {
      return this.$store.getters["liffModule/getConfig"]('member_register', key);
    },
    async sendLiffMessage() {
      // 檢查是否啟用發送 LIFF 訊息功能
      if (!this.getModuleConfig('welcome_page.send_messages.enable')) {
        console.log('LIFF 訊息功能未啟用，跳過發送');
        return;
      }

      try {
        if (!liff.isLoggedIn()) {
          console.log('用戶未登入 LIFF，無法發送訊息');
          return;
        }

        // 獲取訊息內容，如果沒有設定則使用空字串
        let messageText = this.getModuleConfig('welcome_page.send_messages.text') || '';

        // 替換訊息中的客戶資料變數
        if (messageText && this.user) {
          messageText = (new StringVariableParser()).parse(messageText, { customer: this.user });
        }

        const result = await liff.sendMessages([
          {
            type: 'text',
            text: messageText
          }
        ]);

        if (result) {
          console.log('訊息已發送');
        } else {
          console.log('用戶取消了訊息發送');
        }
      } catch (error) {
        console.error('發送 LIFF 訊息失敗:', error);
      }
    },
  },
};
</script>
